/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Button,
  Grid,
  LinearProgress,
  List,
  ListItemButton,
  Box,
} from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import ProductListRow from './ProductListRow';
import type {
  AirmetFromBackend,
  ProductConfig,
  ProductType,
  SigmetFromBackend,
} from '../../types';
import { useSigmetAirmetTranslation } from '../../utils/i18n';

interface ProductListProps {
  productType: ProductType;
  productList: SigmetFromBackend[] | AirmetFromBackend[];
  productConfig: ProductConfig;
  isLoading: boolean;
  error: Error;
  onClickProductRow: (product: SigmetFromBackend | AirmetFromBackend) => void;
  onClickNewProduct: () => void;
}

const ProductList: React.FC<ProductListProps> = ({
  productType,
  productList,
  productConfig,
  isLoading,
  error,
  onClickProductRow,
  onClickNewProduct,
}: ProductListProps) => {
  const { t } = useSigmetAirmetTranslation();

  return (
    <>
      {!!isLoading && (
        <LinearProgress
          color="secondary"
          data-testid="productlist-loadingbar"
          sx={{
            position: 'absolute',
            width: '100%',
            maxWidth: 'inherit',
            left: 0,
            top: 0,
          }}
        />
      )}
      {!!error && (
        <Box
          sx={{
            marginBottom: 2,
            ' .MuiAlert-message': { whiteSpace: 'pre-wrap' },
          }}
        >
          <AlertBanner
            dataTestId="productList-alert"
            info={error.message ? error.message : ''}
            severity="error"
            title={error.name}
          />
        </Box>
      )}
      <Grid alignItems="stretch" container direction="column">
        <Grid container justifyContent="flex-end">
          <Button
            data-testid="productListCreateButton"
            onClick={onClickNewProduct}
            sx={{ textTransform: 'initial' }}
            variant="primary"
          >
            {t('create-new-product', {
              product: productType.toUpperCase(),
            })}
          </Button>
        </Grid>
        <Grid
          sx={{
            width: '100%',
            minHeight: '600px',
          }}
        >
          {!!productList && (
            <List sx={{ paddingTop: 4, containerType: 'inline-size' }}>
              {(productList as (SigmetFromBackend | AirmetFromBackend)[]).map(
                (productListItem) => {
                  const product =
                    productType === 'sigmet'
                      ? (productListItem as SigmetFromBackend)[productType]
                      : (productListItem as AirmetFromBackend)[productType];
                  return (
                    <ListItemButton
                      data-testid="productListItem"
                      key={product.uuid}
                      onClick={(): void => {
                        onClickProductRow(productListItem);
                      }}
                      sx={{ padding: 0.25 }}
                    >
                      <ProductListRow
                        product={product}
                        productConfig={productConfig}
                        productType={productType}
                      />
                    </ListItemButton>
                  );
                },
              )}
            </List>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ProductList;
